"use client";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { formatPrice } from "@/stores/cart-store";
import { guestFacingModifierDescription } from "@/lib/guest-facing-modifier-description";
import type { Id } from "../../../convex/_generated/dataModel";

interface ModifierOption {
  name: string;
  priceAdjustment: number;
  isDefault?: boolean;
  isAvailable: boolean;
}

interface ModifierGroupProps {
  modifier: {
    _id: Id<"menu_modifiers">;
    name: string;
    description?: string;
    selectionType: "single" | "multiple";
    isRequired: boolean;
    minSelections?: number;
    maxSelections?: number;
    options: ModifierOption[];
  };
  selectedOptions: string[];
  onSelectionChange: (optionNames: string[]) => void;
}

export function ModifierGroup({
  modifier,
  selectedOptions,
  onSelectionChange,
}: ModifierGroupProps) {
  const isSingleSelect = modifier.selectionType === "single";
  const availableOptions = modifier.options.filter((opt) => opt.isAvailable);
  const guestDescription = guestFacingModifierDescription(modifier.description);

  // Handle single selection (radio)
  const handleRadioChange = (optionName: string) => {
    onSelectionChange([optionName]);
  };

  // Handle multiple selection (checkbox)
  const handleCheckboxChange = (optionName: string, checked: boolean) => {
    if (checked) {
      // Check max selections
      if (
        modifier.maxSelections &&
        selectedOptions.length >= modifier.maxSelections
      ) {
        return; // Don't add more
      }
      onSelectionChange([...selectedOptions, optionName]);
    } else {
      onSelectionChange(selectedOptions.filter((name) => name !== optionName));
    }
  };

  // Format selection hint
  const getSelectionHint = () => {
    if (isSingleSelect) {
      return "Select 1";
    }
    if (modifier.minSelections && modifier.maxSelections) {
      if (modifier.minSelections === modifier.maxSelections) {
        return `Select ${modifier.minSelections}`;
      }
      return `Select ${modifier.minSelections}-${modifier.maxSelections}`;
    }
    if (modifier.maxSelections) {
      return `Select up to ${modifier.maxSelections}`;
    }
    if (modifier.minSelections) {
      return `Select at least ${modifier.minSelections}`;
    }
    return "Select any";
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 flex-wrap">
          <h4 className="font-semibold text-base text-foreground">
            {modifier.name}
          </h4>
          {modifier.isRequired && (
            <span className="text-[11px] px-2 py-0.5 bg-destructive/10 text-destructive rounded-full font-semibold uppercase tracking-wide">
              Required
            </span>
          )}
        </div>
        <span className="whitespace-nowrap text-sm font-medium text-zinc-600">
          {getSelectionHint()}
        </span>
      </div>

      {/* Description */}
      {guestDescription && (
        <p className="text-sm text-muted-foreground leading-relaxed -mt-1">
          {guestDescription}
        </p>
      )}

      {/* Options Container */}
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden shadow-sm">
        {isSingleSelect ? (
          <RadioGroup
            value={selectedOptions[0] || ""}
            onValueChange={handleRadioChange}
            className="divide-y divide-border/50"
          >
            {availableOptions.map((option) => (
              <label
                key={option.name}
                className="flex items-center gap-4 px-4 py-4 cursor-pointer hover:bg-muted/40 active:bg-muted transition-colors first:rounded-t-xl last:rounded-b-xl touch-manipulation min-h-[60px]"
              >
                <RadioGroupItem 
                  value={option.name} 
                  id={`${modifier._id}-${option.name}`}
                  className="border-2 border-border h-5 w-5 data-[state=checked]:border-foreground data-[state=checked]:bg-foreground"
                />
                <span className="flex-1 text-[15px] font-medium leading-snug text-foreground">
                  {option.name}
                </span>
                {option.priceAdjustment !== 0 ? (
                  <span className="text-[15px] font-semibold text-foreground tabular-nums whitespace-nowrap">
                    {option.priceAdjustment > 0 ? "+" : ""}
                    {formatPrice(option.priceAdjustment)}
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    Included
                  </span>
                )}
              </label>
            ))}
          </RadioGroup>
        ) : (
          <div className="divide-y divide-border/50">
            {availableOptions.map((option) => {
              const isChecked = selectedOptions.includes(option.name);
              const isDisabled =
                !isChecked &&
                modifier.maxSelections !== undefined &&
                selectedOptions.length >= modifier.maxSelections;

              return (
                <label
                  key={option.name}
                  className={`flex items-center gap-4 px-4 py-4 cursor-pointer hover:bg-muted/40 active:bg-muted transition-colors first:rounded-t-xl last:rounded-b-xl touch-manipulation min-h-[60px] ${
                    isDisabled ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <Checkbox
                    id={`${modifier._id}-${option.name}`}
                    checked={isChecked}
                    disabled={isDisabled}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange(option.name, checked === true)
                    }
                    className="border-2 border-border h-5 w-5 data-[state=checked]:border-foreground data-[state=checked]:bg-foreground rounded"
                  />
                  <span className="flex-1 text-[15px] font-medium leading-snug text-foreground">
                    {option.name}
                  </span>
                  {option.priceAdjustment !== 0 && (
                    <span className="text-[15px] font-semibold text-foreground tabular-nums whitespace-nowrap">
                      {option.priceAdjustment > 0 ? "+" : ""}
                      {formatPrice(option.priceAdjustment)}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
